import React, { useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Constants from 'expo-constants';
import messaging from '@react-native-firebase/messaging';

type Row = { k: string; v: string };

function toStr(v: any) {
  if (v === null || v === undefined) return String(v);
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export default function PushDiagnosticsScreen({ navigation }: any) {
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);

  const appOwnership = useMemo(() => {
    return (Constants as any)?.appOwnership ?? 'unknown';
  }, []);

  const add = (k: string, v: any) => {
    setRows((prev) => [{ k, v: toStr(v) }, ...prev]);
  };

  const clear = () => setRows([]);

  const runFirebaseChecks = async () => {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
      add('Platform', 'Not iOS/Android - Firebase Messaging only works on native');
      return;
    }

    add('Platform', Platform.OS);
    add('AppOwnership', appOwnership);
    add('EAS Project ID', (Constants as any)?.expoConfig?.extra?.eas?.projectId ?? 'N/A');
    add('iOS Bundle ID', (Constants as any)?.expoConfig?.ios?.bundleIdentifier ?? 'N/A');
    add('Android Package', (Constants as any)?.expoConfig?.android?.package ?? 'N/A');

    // 1. Check Permission Status
    try {
      const authStatus = await messaging().hasPermission();
      const statusText =
        authStatus === messaging.AuthorizationStatus.AUTHORIZED
          ? 'AUTHORIZED'
          : authStatus === messaging.AuthorizationStatus.PROVISIONAL
          ? 'PROVISIONAL'
          : authStatus === messaging.AuthorizationStatus.NOT_DETERMINED
          ? 'NOT_DETERMINED'
          : 'DENIED';
      add('Firebase hasPermission()', `${statusText} (${authStatus})`);
    } catch (e: any) {
      add('Firebase hasPermission() ERROR', e?.message ?? e);
    }

    // 2. Request Permission
    try {
      const authStatus = await messaging().requestPermission();
      const statusText =
        authStatus === messaging.AuthorizationStatus.AUTHORIZED
          ? 'AUTHORIZED'
          : authStatus === messaging.AuthorizationStatus.PROVISIONAL
          ? 'PROVISIONAL'
          : 'DENIED/OTHER';
      add('Firebase requestPermission()', `${statusText} (${authStatus})`);
    } catch (e: any) {
      add('Firebase requestPermission() ERROR', e?.message ?? e);
    }

    // 3. Get FCM Token
    try {
      const fcmToken = await messaging().getToken();
      if (fcmToken) {
        add('Firebase FCM Token', `[REDACTED - Length: ${fcmToken.length}]`);
      } else {
        add('Firebase FCM Token', 'NULL (not registered)');
      }
    } catch (e: any) {
      add('Firebase getToken() ERROR', e?.message ?? e);
    }

    // 4. Get APNs Token (iOS only)
    if (Platform.OS === 'ios') {
      try {
        const apnsToken = await messaging().getAPNSToken();
        if (apnsToken) {
          add('Firebase APNs Token', `[REDACTED - Length: ${apnsToken.length}]`);
        } else {
          add('Firebase APNs Token', 'NULL (not registered with APNs yet)');
        }
      } catch (e: any) {
        add('Firebase getAPNSToken() ERROR', e?.message ?? e);
      }
    }

    // 5. Check Auto-Init
    try {
      const autoInit = await messaging().isAutoInitEnabled;
      add('Firebase Auto-Init Enabled', autoInit ? 'true' : 'false');
    } catch (e: any) {
      add('Firebase isAutoInitEnabled ERROR', e?.message ?? e);
    }
  };

  const runAll = async () => {
    setBusy(true);
    clear();
    try {
      add('START', new Date().toISOString());
      await runFirebaseChecks();
      add('DONE', new Date().toISOString());
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    navigation.setOptions({
      title: 'Firebase Push Diagnostics',
      headerStyle: {
        backgroundColor: '#3949ab',
      },
      headerTintColor: '#ffffff',
      headerTitleStyle: {
        color: '#ffffff',
        fontWeight: '700',
      },
    });
  }, [navigation]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>🔥 Firebase Push Diagnostics</Text>

      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.btn, busy && styles.btnDisabled]}
          disabled={busy}
          onPress={runAll}
        >
          <Text style={styles.btnText}>{busy ? 'Running...' : 'Run Diagnostics'}</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={clear}>
          <Text style={styles.btnText}>Clear</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.hint}>
        📝 Firebase Cloud Messaging Only{'\n'}
        ━━━━━━━━━━━━━━━━━━{'\n'}✅ FCM Token required{'\n'}✅ APNs Token (iOS){'\n'}✅ Permission
        AUTHORIZED{'\n'}
        {'\n'}🔧 If NULL:{'\n'}- Check Firebase init{'\n'}- Run verify script{'\n'}
        {'\n'}📸 Screenshot for help
      </Text>

      <ScrollView style={styles.box} contentContainerStyle={{ padding: 12 }}>
        {rows.length === 0 ? (
          <Text style={styles.empty}>اضغط Run Diagnostics للبدء</Text>
        ) : (
          rows.map((r, idx) => (
            <View key={idx} style={styles.row}>
              <Text style={styles.k}>{r.k}</Text>
              <Text style={styles.v}>{r.v}</Text>
            </View>
          ))
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16, backgroundColor: '#f1f5f9' },
  title: { fontSize: 18, fontWeight: '800', marginBottom: 10, color: '#1e293b' },
  actions: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  btn: {
    backgroundColor: '#3949ab',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    flex: 1,
  },
  btnSecondary: { backgroundColor: '#64748b' },
  btnDisabled: { opacity: 0.6 },
  btnText: { color: 'white', fontWeight: '700', textAlign: 'center' },
  hint: { color: '#334155', lineHeight: 18, marginBottom: 10, fontSize: 12 },
  box: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    backgroundColor: '#0b1220',
  },
  empty: { color: '#94a3b8', textAlign: 'center', marginTop: 20 },
  row: { marginBottom: 14, borderBottomWidth: 1, borderBottomColor: '#1e293b', paddingBottom: 8 },
  k: { color: '#93c5fd', fontWeight: '800', marginBottom: 4, fontSize: 13 },
  v: {
    color: '#e2e8f0',
    fontSize: 12,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
});
